import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import clone from "../../helpers/clone";
import Paper from "@material-ui/core/Paper";
import Checkbox from "@material-ui/core/Checkbox";
import Tooltip from "@material-ui/core/Tooltip";
import Table from "@material-ui/core/Table";
import TableHeader from "../material-components/Table/TableHeader";
import TableBody from "../material-components/Table/TableBody";
import LoadingTable from "../material-components/WidgetSkeleton/LoadingTable";
import { stableSort, getSorting, filterData } from "../../helpers/materialTableFunctions";
import { IUserCompanyFeature } from "../../types/stores/feature";
import { IAppState } from "../../types/stores/index";

type Props = {
    isModal: boolean;
    filteredFeatures: Array<IUserCompanyFeature>;
    selectedFeatures: Array<IUserCompanyFeature>;
    filter: string;
    setFilteredFeatures: (filteredFeatures: Array<IUserCompanyFeature>) => void;
    setSelectedFeatures: (selectedFeatures: Array<IUserCompanyFeature>) => void;
    usersLoading: boolean;
};

const FeaturesCurrCompanyTable: React.FC<Props> = ({
    filteredFeatures,
    selectedFeatures,
    filter,
    setFilteredFeatures,
    setSelectedFeatures,
    isModal,
    usersLoading,
}: Props) => {
    const [isSuperAdmin, setSuperAdmin] = useState(false);
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("Name");

    const loading = useSelector((state: IAppState) => state.companyEdit.isLoadingFeatures || usersLoading);
    const adminRoles = useSelector((state: IAppState) => state.session.roles || []);

    useEffect(() => {
        setSuperAdmin(adminRoles.includes("Super Admin"));
    }, [adminRoles]);

    // functions
    const getTableData = () => {
        const tableData: Array<Array<Array<string | JSX.Element | null> | boolean>> = [];
        if (filteredFeatures && filteredFeatures.length > 0) {
            stableSort(filteredFeatures, getSorting(order, orderBy)).map((dataRow: IUserCompanyFeature) =>
                tableData.push(
                    isModal
                        ? [
                              ["UserSelected", createCheckbox(dataRow, "user"), "xsDown", "center"],
                              ["CompanySelected", createCheckbox(dataRow, "company"), "xsDown", "center"],
                              ["FeatureName", dataRow.Name, "smDown", "left"],
                              ["FeatureDescription", createDescriptionCell(dataRow.Description), "smDown", "left"],
                              isSuperAdmin && ["IsExternal", isExternalIcon(dataRow.IsExternal), "xsDown"],
                          ]
                        : [
                              ["FeatureActive", createCheckbox(dataRow), "xsDown", "right"],
                              ["FeatureName", dataRow.Name, "smDown", "left"],
                              ["FeatureDescription", createDescriptionCell(dataRow.Description), "smDown", "left"],
                              isSuperAdmin && ["IsExternal", isExternalIcon(dataRow.IsExternal), "xsDown"],
                          ]
                )
            );
        }
        return tableData;
    };

    const handleRequestSort = (_, property) => {
        setOrder(order == "asc" ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleCheckBox = (dataRow: IUserCompanyFeature, type: string) => {
        let newFeatures: Array<IUserCompanyFeature> = clone(selectedFeatures);
        const filteredIndex = newFeatures.map(x => x.Name).indexOf(dataRow.Name);
        if (type == "user") {
            newFeatures[filteredIndex].UserSelected = !dataRow.UserSelected;
        } else if (type == "company") {
            newFeatures[filteredIndex].CompanySelected = !dataRow.CompanySelected;
        }
        setFilteredFeatures(filterData(newFeatures, filter, null));
        setSelectedFeatures(newFeatures);
    };

    const createCheckbox = (dataRow: IUserCompanyFeature, type = "company") => {
        const checked = type == "user" ? dataRow.UserSelected : dataRow.CompanySelected;
        const disabled = type == "company" && dataRow.IsUserOnly;

        return (
            <Checkbox
                checked={checked}
                value={dataRow.Name}
                onClick={() => handleCheckBox(dataRow, type)}
                inputProps={{ "aria-label": Boolean(checked).toString() + "-" + dataRow.Name }}
                icon={
                    <i className="material-icons" style={{ fontSize: 16 }}>
                        check_box_outline_blank
                    </i>
                }
                checkedIcon={
                    <i className="material-icons" style={{ fontSize: 16 }}>
                        check_box
                    </i>
                }
                disabled={disabled}
                style={{ color: disabled ? "#575757" : "#406eb3", height: 16, width: 16, margin: "0px 10px" }}
                color="primary"
            />
        );
    };

    const createDescriptionCell = value => (
        <Tooltip title={value}>
            <span>{value}</span>
        </Tooltip>
    );

    const isExternalIcon = isExternal =>
        isExternal ? (
            <i
                className="material-icons"
                style={{
                    height: "15px",
                    width: "15px",
                    fontSize: "15px",
                    fontWeight: "bold",
                    color: "#406eb3",
                }}
            >
                check
            </i>
        ) : null;

    // variables
    const customLeadingColumnWidth = isModal ? "45px" : "25px";
    const rowsPerPage = filteredFeatures == null ? 0 : filteredFeatures.length;
    const tableHeader = isModal
        ? [
              ["UserSelected", "User", "xsDown", "center"],
              ["CompanySelected", "Company", "xsDown", "center"],
              ["Name", "Name", "smDown", "left"],
              ["Description", "Description", "smDown", "left"],
              isSuperAdmin && ["IsExternal", "Is External", "xsDown"],
          ]
        : [
              ["Selected", "Active", "xsDown", "right"],
              ["Name", "Name", "smDown", "left"],
              ["Description", "Description", "smDown", "left"],
              isSuperAdmin && ["IsExternal", "Is External", "xsDown"],
          ];
    const emptyMessage = ["EmptyMessage", "No Records to Display.", "smDown"];

    return (
        <Paper
            style={
                !isModal
                    ? {
                          margin: 0,
                          paddingTop: 0,
                          height: "70vh",
                          overflow: "auto",
                      }
                    : {
                          height: "50vh",
                          overflow: "auto",
                      }
            }
        >
            <Table style={{ marginTop: 0 }} size={"medium"} stickyHeader>
                <TableHeader
                    headers={tableHeader}
                    onClick={handleRequestSort}
                    orderBy={orderBy}
                    order={order}
                    customLeadingColumnWidth={customLeadingColumnWidth}
                />
                {loading ? (
                    <LoadingTable cols={isSuperAdmin ? (isModal ? 5 : 4) : isModal ? 4 : 3} rows={35} />
                ) : (
                    <TableBody
                        data={getTableData()}
                        customLeadingColumnWidth={customLeadingColumnWidth}
                        page={0}
                        rowsPerPage={rowsPerPage}
                        emptyMessage={emptyMessage}
                    />
                )}
            </Table>
        </Paper>
    );
};

export default FeaturesCurrCompanyTable;
